import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Medication } from 'src/app/calvin-emr-shared/models/medication.model';
import { MedicationService } from 'src/app/calvin-emr-shared/services/medication.service';
import {
  optionACHS,
  optionBID,
  optionPRN,
  optionQD,
  optionTID,
  optionNOW,
  optionSTAT,
} from 'src/app/calvin-emr-shared/global';
import { FormOption } from 'src/app/shared/models/form-option.model';
import { ProviderOrder, MedicationAdminTime, MedicationAdminWindow, SlidingScaleOrder, MealTimesOrder } from 'src/app/calvin-emr-shared/models/provider-order.model';
import { Select } from 'primeng/select';
import { EmrInputComponent } from "../../../../../shared/emr-input/emr-input.component";
import { TableModule } from 'primeng/table';
import { Button, ButtonDirective } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

interface SlidingScaleOrderPlusKey extends SlidingScaleOrder {
  key: number;
}

interface MealTimesOrderPlusKey extends MealTimesOrder {
  key: number;
}

@Component({
  selector: 'app-order-form',
  templateUrl: './order-form.component.html',
  styleUrls: ['./order-form.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Select,
    EmrInputComponent,
    TableModule,
    ButtonDirective,
    Button,
    DialogModule,
  ],
})
export class OrderFormComponent implements OnInit, OnDestroy {
  /**
   * A ProviderOrder used to prefill the form. For example,
   * if an order is being edited by the user.
   */
  @Input() order?: ProviderOrder;

  /**
   * The list of all medications.
   */
  medications: Medication[] = [];

  /**
   * The list of all medications along with their IDs concatenated in a string.
   */
  medicationOptions: string[] = [];

  /**
   * Form used to hold information about a provider order.
   * See function validateSometimesReqdFields which checks
   * if some fields are filled in certain times.
   */
  form = this.fb.group({
    medication: ['', Validators.required],
    medControlledSubstance: [''],
    route: ['', Validators.required],
    dosage: [''],
    type: ['', Validators.required],
    startDate: [''],
    prnLastGivenTime: [''],
    prnDosingInstructions: [''],
    order: [''],
    signature: ['', Validators.required],
  }, { validators: validateSometimesReqdFields });

  orderTypeOptions: FormOption[] = [
    new FormOption(optionSTAT),
    new FormOption(optionNOW),
    new FormOption(optionQD),
    new FormOption(optionBID),
    new FormOption(optionTID),
    new FormOption(optionACHS),
    new FormOption(optionPRN),
  ];

  medicationSubscription!: Subscription;

  // SlidingScaleOrder with a key in each object
  slidingScaleTable: SlidingScaleOrderPlusKey[] = [
    { key: 0, bs: '0-140', units: 0 },
    { key: 1, bs: '141-155', units: 2 },
    { key: 2, bs: '156-170', units: 4 },
    { key: 3, bs: '171-185', units: 5 },
    { key: 4, bs: '186-200', units: 6 },
    { key: 5, bs: '> 200', units: 'Call Provider' },
  ];

  // MealTimesOrder with a key in each object
  mealTimesTable: MealTimesOrderPlusKey[] = [
    { key: 0, carbs: '15', units: 1 },
    { key: 1, carbs: '30', units: 2 },
    { key: 2, carbs: '45', units: 3 },
    { key: 3, carbs: '60', units: 4 },
    { key: 4, carbs: '70', units: 5 },
  ];

  slidingScaleNewRowDialog = false;
  mealTimesNewRowDialog = false;

  slidingScaleBsFormControl = new UntypedFormControl('');
  slidingScaleUnitsFormControl = new UntypedFormControl('');

  /**
   * Gets all medications from Firestore and initializes the filter for the
   * 'Medication Name' form input.
   *
   * @param fb FormBuilder used to create the providerOrderForm.
   * @param medService Service used to retrieve medications from Firestore.
   */
  constructor(private fb: UntypedFormBuilder, private medService: MedicationService) { }

  ngOnInit(): void {
    // Get all medications from backend
    this.medicationSubscription = this.medService.getAllMedications().subscribe((res) => {
      this.medications = res.map((item) => {
        const med = new Medication();
        const obj = {
          id: item.payload.doc.id,
          ...(item.payload.doc.data() as object),
        };
        Object.assign(med, obj);
        return med;
      });

      this.medicationOptions = this.medications.map((med) => {
        return `${med.name} (${med.id})`;
      });

      this.prefillForm();
    });
  }

  ngOnDestroy(): void {
    this.medicationSubscription.unsubscribe();
  }

  /**
   * If the form is specified to be prefilled (for example, when an order is being edited),
   * set all values in the form to match those of the provided ProviderOrder.
   * ngOnInit must be used to set the form; otherwise, the form's value will be set before
   * the Order object is passed to the OrderForm component.
   */
  prefillForm() {
    // For pre-filling form in OrderEditDialogComponent:
    //  If an order is provided, fill the form fields with their respective values
    if (this.isPrefilled && this.order) {
      const date = new Date();
      date.setHours(this.order.startTime.hour, this.order.startTime.minute);
      this.startDateFormControl.setValue(date);

      if (this.order.isPRN) {
        if (this.order.prnLastGivenTime) {
          const date = new Date();
          date.setHours(this.order.prnLastGivenTime!.hour, this.order.prnLastGivenTime!.minute);
          this.prnLastGivenTimeFormControl.setValue(date);
        }

        this.prnDosingInstrFormControl.setValue(this.order.prnDosingInstructions);
      }

      this.form.patchValue({
        medication: this.medications.find((med) => med.id === this.order?.medId),
        medControlledSubstance: this.order.medControlledSubstance,
        route: this.order.route,
        dosage: this.order?.dosage,
        type: this.order.type,
        order: this.order?.orderNotes,
        signature: this.order?.signature,
      });
    }
  }


  /**
   * Retrieves and formats data from the form to create a ProviderOrder object.
   *
   * @returns A new ProviderOrder with values taken from the form.
   */
  public createOrderFromForm(options?: { partOfOrderSet: boolean }): ProviderOrder {

    // Get medication information
    const med = this.medicationFormControl.value as Medication;
    const medId = med.getId();
    const medName = med.getName();
    const medControlledSubstance = med.getControlledSubstance();

    // Remove whitespace
    const dosage = this.form.value.dosage.trim();
    const orderNotes = this.form.value.order.trim();
    const signature = this.form.value.signature.trim();
    const prnDosingInstructions = this.form.value.prnDosingInstructions.trim();

    // Create variables to use to store pre-filled or default data
    let id, quantityGiven, dateCreated;
    // Set variable values based on whether form is pre-filled or not
    if (this.isPrefilled && this.order) {
      id = this.order.id;
      quantityGiven = this.order.quantityGiven;
    } else {
      // Set order ID to be the date in milliseconds
      id = '' + Date.now();
      quantityGiven = 0;
    }

    let startTime: MedicationAdminTime = {
      hour: new Date().getHours(),
      minute: new Date().getMinutes(),
    };
    if (this.isQD) {
      const d = new Date(this.startDateFormControl.value);
      startTime = {
        hour: d.getHours(),
        minute: d.getMinutes(),
      };
    }
    let prnLastGivenTime = null;
    if (this.isPRN && this.prnLastGivenTimeFormControl.value) {
      const d = new Date(this.prnLastGivenTimeFormControl.value);
      prnLastGivenTime = {
        hour: d.getHours(),
        minute: d.getMinutes(),
      };
    }

    let quantity = 1;   // for QD, STAT, NOW, etc.
    if (this.isACHS) {
      quantity = 4;
    } else if (this.isBID) {
      quantity = 2;
    } else if (this.isTID) {
      quantity = 3;
    }

    // Initially, no medicines have been given to the patient.
    const whichGiven = new Array(quantity).fill(false);

    // for each object in the array, remove the "key" property
    function removeKeyField(tab: { key: number }[]) {
      return tab.map(({ key: number, ...rest }) => {
        return rest;
      })
    }

    let partOfOrderSet = false;
    let isScheduled = true;
    if (options && options.partOfOrderSet) {
      partOfOrderSet = true;
      isScheduled = false;
    }

    return new ProviderOrder(
      id,
      medId,
      medName,
      medControlledSubstance,
      this.routeFormControl.value,
      dosage,
      quantity,
      quantityGiven,
      whichGiven,
      this.orderTypeFormControl.value,
      orderNotes,
      signature,
      startTime,
      prnLastGivenTime,
      prnDosingInstructions,
      this.isSlidingScale ? removeKeyField(this.slidingScaleTable) as SlidingScaleOrder[] : [],
      this.isMealTimes ? removeKeyField(this.mealTimesTable) as MealTimesOrder[] : [],
      partOfOrderSet,   // isPartOfOrderSet field
      isScheduled,      // isScheduled: show on MAR schedule.
    );
  }

  /**
   * Gets the possible routes associated with the selected medication to display
   * as options in the form.
   */
  get medRoutes(): string[] {
    if (this.medicationFormControl.value === null || this.medicationFormControl.value === '') {
      return [];
    }

    return (this.medicationFormControl.value as Medication).routes;
  }

  get medControlledSubstance(): boolean {
    return (this.medicationFormControl.value as Medication).controlledSubstance;
  }

  get isPrefilled() {
    return this.order != null;
  }

  get isQD() {
    return this.orderTypeFormControl.value === optionQD;
  }
  get isBID() {
    return this.orderTypeFormControl.value === optionBID;
  }
  get isTID() {
    return this.orderTypeFormControl.value === optionTID;
  }
  get isACHS() {
    return this.orderTypeFormControl.value === optionACHS;
  }
  get isSTAT() {
    return this.orderTypeFormControl.value === optionSTAT;
  }
  get isNOW() {
    return this.orderTypeFormControl.value === optionNOW;
  }
  get isPRN() {
    return this.orderTypeFormControl.value === optionPRN;
  }

  get isTypeSet() {
    return this.orderTypeFormControl.value !== '';
  }

  get isMealTimesOrSlidingScale() {
    return this.medicationFormControl.value.mealTimes || this.medicationFormControl.value.slidingScale;
  }
  get isMealTimes() {
    return this.medicationFormControl.value.mealTimes;
  }
  get isSlidingScale() {
    return this.medicationFormControl.value.slidingScale;
  }

  get didSelectMedication() {
    return (
      this.medicationFormControl.value !== undefined &&
      this.medicationFormControl.value !== null &&
      this.medicationFormControl.value !== ''
    );
  }

  get medicationFormControl(): UntypedFormControl {
    return this.form.controls.medication as UntypedFormControl;
  }

  get routeFormControl(): UntypedFormControl {
    return this.form.controls.route as UntypedFormControl;
  }

  get dosageFormControl(): UntypedFormControl {
    return this.form.controls.dosage as UntypedFormControl;
  }

  get orderTypeFormControl(): UntypedFormControl {
    return this.form.controls.type as UntypedFormControl;
  }

  get startDateFormControl(): UntypedFormControl {
    return this.form.controls.startDate as UntypedFormControl;
  }

  get prnLastGivenTimeFormControl(): UntypedFormControl {
    return this.form.controls.prnLastGivenTime as UntypedFormControl;
  }

  get prnDosingInstrFormControl(): UntypedFormControl {
    return this.form.controls.prnDosingInstructions as UntypedFormControl;
  }


  get orderFormControl(): UntypedFormControl {
    return this.form.controls.order as UntypedFormControl;
  }

  get signatureFormControl(): UntypedFormControl {
    return this.form.controls.signature as UntypedFormControl;
  }

  get medControlledSubstanceFormControl(): UntypedFormControl {
    return this.form.controls.controlledSubstance as UntypedFormControl;
  }

  // This code is super similar to code in provider-order.model.ts, but slightly different in a few places.
  // Would be nice to merge these sometime...

  /**
   * for a order of a certain type, return an array of MedicationAdminWindows
   * If orderType is QD, the startTime must be provided.
   */
  providerOrder2MedAdminWindows(orderType: string, startTime?: Date): MedicationAdminWindow[] {
    if (orderType === optionBID) {
      return [
        { hour: 9, minute: 0, window: 60 },
        { hour: 21, minute: 0, window: 60 },
      ];
    } else if (orderType === optionTID) {
      return [
        { hour: 9, minute: 0, window: 60 },
        { hour: 16, minute: 0, window: 60 },
        { hour: 21, minute: 0, window: 60 },
      ];
    } else if (orderType === optionACHS) {
      return [
        { hour: 7, minute: 30, window: 60 },
        { hour: 11, minute: 30, window: 60 },
        { hour: 17, minute: 0, window: 60 },
        { hour: 22, minute: 0, window: 60 },
      ];
    } else if (orderType === optionQD) {
      return [{
        hour: startTime!.getHours(),
        minute: startTime!.getMinutes(),
        window: 60,
      }];
    } else {
      // STAT, NOW, etc.
      const now = new Date();
      return [{
        hour: now.getHours(),
        minute: now.getMinutes(),
        window: orderType === optionSTAT ? 30 : 60,
      }];
    }
  }

  /**
   * @returns array of readable strings describing the times to administer the meds.
   */
  providerOrder2MedSchedule(): string[] {

    // create a string like 09:00 - 10:00 or 13:41 - 14:11
    const order2DurationString = (sched: MedicationAdminWindow): string => {
      const startDate = new Date();
      startDate.setHours(sched.hour, sched.minute, 0);

      const endDate = new Date(startDate);
      // Take the start date and add the administer window (in minutes) to it.
      endDate.setTime(startDate.getTime() + sched.window * 60 * 1000);

      return `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')} - ` +
        `${(endDate.getHours()).toString().padStart(2, '0')}:${(endDate.getMinutes()).toString().padStart(2, '0')}`;
    }

    // If the user has not filled in the time for 'QD', don't show anything.
    if (this.orderTypeFormControl.value === optionQD &&
      this.startDateFormControl.value === '') {
      return [''];
    };

    const sched = this.providerOrder2MedAdminWindows(this.orderTypeFormControl.value,
      new Date(this.startDateFormControl.value));
    return sched.map((s: MedicationAdminWindow, idx: number) => {
      return `Dose ${idx + 1} scheduled for ${order2DurationString(s)}`;
    });
  }


  // Backup copies of rows so that if the user cancels the edit we can restore the original
  // (User can edit multiple rows at once.)
  backupSlidingScaleRows: { [key: number]: SlidingScaleOrderPlusKey } = {};

  onSlidingScaleRowEditInit(slidingScaleValue: SlidingScaleOrderPlusKey) {
    this.backupSlidingScaleRows[slidingScaleValue.key] = { ...slidingScaleValue };
  }
  onSlidingScaleRowEditCancel(slidingScaleValue: SlidingScaleOrderPlusKey, tableIdx: number) {
    // restore from backup copy.
    this.slidingScaleTable[tableIdx] = this.backupSlidingScaleRows[slidingScaleValue.key];
    delete this.backupSlidingScaleRows[slidingScaleValue.key];
  }
  onSlidingScaleRowEditSave(slidingScaleValue: SlidingScaleOrderPlusKey) {
    // done editing, and new data is in the table, so just delete backup.
    delete this.backupSlidingScaleRows[slidingScaleValue.key];
  }
  onSlidingScaleRowDelete(tableIdx: number) {
    this.slidingScaleTable.splice(tableIdx, 1);
  }

  // Adds a new empty row, which can then be edited.
  onSlidingScaleNewRowAdd() {
    this.slidingScaleTable.push({
      key: this.slidingScaleTable.length,
      bs: '',
      units: '',
    });
  }

  // Backup copies of rows so that if the user cancels the edit we can restore the original
  // (User can edit multiple rows at once.)
  backupMealTimesRows: { [key: number]: MealTimesOrderPlusKey } = {};

  onMealTimesRowEditInit(mealTimesValue: MealTimesOrderPlusKey) {
    this.backupMealTimesRows[mealTimesValue.key] = { ...mealTimesValue };
  }
  onMealTimesRowEditCancel(mealTimesValue: MealTimesOrderPlusKey, tableIdx: number) {
    // restore from backup copy.
    this.mealTimesTable[tableIdx] = this.backupMealTimesRows[mealTimesValue.key];
    delete this.backupMealTimesRows[mealTimesValue.key];
  }
  onMealTimesRowEditSave(mealTimesValue: MealTimesOrderPlusKey) {
    // done editing, and new data is in the table, so just delete backup.
    delete this.backupMealTimesRows[mealTimesValue.key];
  }
  onMealTimesRowDelete(tableIdx: number) {
    this.mealTimesTable.splice(tableIdx, 1);
  }

  // Adds a new empty row, which can then be edited.
  onMealTimesNewRowAdd() {
    this.mealTimesTable.push({
      key: this.mealTimesTable.length,
      carbs: '',
      units: 0,
    });
  }

}



export const validateSometimesReqdFields: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {

  // if PRN is chosen, then prnDosingInstructions is required.
  // if QD is chosen, then startDate is rquired.
  // if the medication does not have "mealtimes" or "sliding scale" selected,
  // then dosage is required.

  // All other required fields are handled in the form definition above.

  const type = control.get('type')?.value;

  if (type === optionPRN) {
    const prnDosingInstructions = control.get('prnDosingInstructions')?.value;
    if (!prnDosingInstructions) {
      return { prnDosingMissing: true };
    }
  } else if (type === optionQD) {
    const startDate = control.get('startDate')?.value;
    if (!startDate) {
      return { QDstartDateMissing: true };
    }
  }

  // if meal times or sliding scale, no dosage required.
  const med = control.get('medication') as UntypedFormControl;
  const isMealtimesOrSlidingScale = med.value.mealTimes || med.value.slidingScale;

  if (!isMealtimesOrSlidingScale) {
    const dosage = control.get('dosage')?.value;
    if (!dosage) {
      return { 'no dosage for insulin': true };
    }
  }
  return null;
}